import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import {
  getAllContactMessagesAPI,
  updateContactMessageStatusAPI,
  deleteContactMessageAPI,
} from "../../helpers/api.ts";

import { useNavigate } from "react-router-dom";
 
const ContactUsListing = () => {
  const navigate = useNavigate();
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");

  const itemsPerPage = 10;

  useEffect(() => {
    fetchContactMessages();
  }, [currentPage, statusFilter]);

  const fetchContactMessages = async () => {
    try {
      setLoading(true);
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      status: statusFilter || undefined,
    };

    const response = await getAllContactMessagesAPI(params);
    
    if (response.success) {
      setContactMessages(response.data.contactMessages);
      setTotalPages(response.data.pages);
      setTotalItems(response.data.total);
    } else {
      setAlertMessage("Failed to fetch contact messages");
      setAlertType("danger");
    }
  } catch (error) {
    console.error("Error fetching contact messages:", error);
    setAlertMessage("Error fetching contact messages");
    setAlertType("danger");
  } finally {
    setLoading(false);
  }
};

const handleStatusUpdate = async (id, newStatus) => {
  try {
    const response = await updateContactMessageStatusAPI(id, newStatus);
    if (response.success) {
      setAlertMessage("Status updated successfully");
      setAlertType("success");
      fetchContactMessages();
    } else {
      setAlertMessage("Failed to update status");
      setAlertType("danger");
    }
  } catch (error) {
    console.error("Error updating status:", error);
    setAlertMessage("Error updating status");
    setAlertType("danger");
  }
};

const handleDelete = async () => {
  if (!messageToDelete) return;
  
  try {
    const response = await deleteContactMessageAPI(messageToDelete.id);
    if (response.success) {
      setAlertMessage("Message deleted successfully");
      setAlertType("success");
      fetchContactMessages();
      setDeleteModalOpen(false);
      setMessageToDelete(null);
    } else {
      setAlertMessage("Failed to delete message");
      setAlertType("danger");
    }
  } catch (error) {
    console.error("Error deleting message:", error);
    setAlertMessage("Error deleting message");
    setAlertType("danger");
  }
};

const openModal = (message) => {
  setSelectedMessage(message);
  setModalOpen(true);
};

const openDeleteModal = (message) => {
  setMessageToDelete(message);
  setDeleteModalOpen(true);
};

const getStatusBadge = (status) => {
  const statusConfig = {
    NEW: { color: "primary", text: "New" },
    INPROGRESS: { color: "warning", text: "In Progress" },
    CLOSED: { color: "success", text: "Closed" },
  };
  
  const config = statusConfig[status] || { color: "secondary", text: status };
  return <Badge color={config.color}>{config.text}</Badge>;
};

const filteredMessages = contactMessages.filter(message => {
  if (!searchTerm) return true;
  return (
    message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.message.toLowerCase().includes(searchTerm.toLowerCase())
  );
});

const handlePageChange = (page) => {
  setCurrentPage(page);
};

const renderPagination = () => {
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(
      <PaginationItem key={i} active={i === currentPage}>
        <PaginationLink onClick={() => handlePageChange(i)}>
          {i}
        </PaginationLink>
      </PaginationItem>
    );
  }
  return pages;
};

return (
  <div className="page-content">
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Contact Messages</h4>
          </div>
        </div>
      </div>

      {alertMessage && (
        <Alert color={alertType} className="mt-3">
          {alertMessage}
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="row mb-3 g-2">
                <Col xs={12} md={4}>
                  <FormGroup>
                    {/* <Label>Status Filter</Label> */}
                    <Input
                      type="select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="NEW">New</option>
                      <option value="INPROGRESS">In Progress</option>
                      <option value="CLOSED">Closed</option>
                    </Input>
                  </FormGroup>
                </Col>
                {/* <Col md={4}>
                  <FormGroup>
                    <Label>Search</Label>
                    <Input
                      type="text"
                      placeholder="Search by email or message..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </FormGroup>
                </Col> */}
                <Col xs={12} md={4} className="d-flex align-items-start">
                  <Button
                    color="primary"
                    onClick={fetchContactMessages}
                    disabled={loading}
                    style={{height:'fit-content'}}
                    className="w-100 w-md-auto"
                  >
                    {loading ? <Spinner size="sm" /> : "Refresh"}
                  </Button>
                </Col>
              </div>

              <div className="table-responsive">
                <Table className="table table-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <Spinner size="sm" /> Loading...
                        </td>
                      </tr>
                    ) : filteredMessages.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No contact messages found
                        </td>
                      </tr>
                    ) : (
                      filteredMessages.map((message) => (
                        <tr key={message.id}>
                          <td>{message.id}</td>
                          <td>{message.email}</td>
                          <td>
                            {message.message.length > 50
                              ? `${message.message.substring(0, 50)}...`
                              : message.message}
                          </td>
                          <td>{getStatusBadge(message.status)}</td>
                          <td>
                            {new Date(message.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                color="info"
                                onClick={() => openModal(message)}
                              >
                                View
                              </Button>
                              {message.status !== "CLOSED" && (
                                <Button
                                  size="sm"
                                  color="success"
                                  onClick={() =>
                                    handleStatusUpdate(message.id, "CLOSED")
                                  }
                                >
                                  Close
                                </Button>
                              )}
                              {message.status === "NEW" && (
                                <Button
                                  size="sm"
                                  color="warning"
                                  onClick={() =>
                                    handleStatusUpdate(message.id, "INPROGRESS")
                                  }
                                >
                                  In Progress
                                </Button>
                              )}
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => openDeleteModal(message)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink
                        previous
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                    </PaginationItem>
                    {renderPagination()}
                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink
                        next
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}

              <div className="mt-3">
                <p className="text-muted">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} entries
                </p>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* View Message Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" className="modal-fullscreen-md-down">
        <ModalHeader toggle={() => setModalOpen(false)}>
          Contact Message Details
        </ModalHeader>
        <ModalBody>
          {selectedMessage && (
            <div>
              <Row>
                <Col md={6}>
                  <strong>ID:</strong> {selectedMessage.id}
                </Col>
                <Col md={6}>
                  <strong>Status:</strong> {getStatusBadge(selectedMessage.status)}
                </Col>
              </Row>
              <hr />
              <Row>
                <Col md={12}>
                  <strong>Email:</strong>
                  <p>{selectedMessage.email}</p>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <strong>Message:</strong>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {selectedMessage.message}
                  </p>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <strong>Created At:</strong>
                  <p>{new Date(selectedMessage.createdAt).toLocaleString()}</p>
                </Col>
                <Col md={6}>
                  <strong>Updated At:</strong>
                  <p>{new Date(selectedMessage.updatedAt).toLocaleString()}</p>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        className="modal-fullscreen-md-down"
      >
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this contact message? This action
          cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  </div>
);
};

export default ContactUsListing;
